import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { teams } from "@/mocks/teams";
import { matches } from "@/mocks/matches";

interface SearchBoxProps {
  variant?: "desktop" | "mobile";
  onNavigate?: () => void;
}

export default function SearchBox({ variant = "desktop", onNavigate }: SearchBoxProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return { teams: [], matches: [] };
    const matchedTeams = teams.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.city.toLowerCase().includes(q) ||
        t.abbreviation.toLowerCase().includes(q),
    );
    const matchedMatches = matches
      .filter((m) => {
        const home = teams.find((t) => t.id === m.homeTeamId);
        const away = teams.find((t) => t.id === m.awayTeamId);
        if (!home || !away) return false;
        return `${home.name}${away.name}`.toLowerCase().includes(q) || m.stage.toLowerCase().includes(q);
      })
      .slice(0, 4);
    return { teams: matchedTeams.slice(0, 4), matches: matchedMatches };
  }, [query]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const go = (to: string) => {
    setOpen(false);
    setQuery("");
    onNavigate?.();
    navigate(to);
  };

  const hasResults = results.teams.length > 0 || results.matches.length > 0;

  return (
    <div ref={boxRef} className="relative w-full">
      <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 focus-within:border-primary-400">
        <i className="ri-search-line text-foreground-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="搜索赛事、球队…"
          className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
        />
      </div>

      {open && query.trim() && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-80 overflow-y-auto rounded-md border border-background-300 bg-background-50 py-1">
          {!hasResults && (
            <p className="px-3 py-3 text-sm text-foreground-500">未找到相关赛事或球队</p>
          )}
          {results.teams.length > 0 && (
            <div className="py-1">
              <p className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground-400">球队</p>
              {results.teams.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => go(`/team/${t.id}`)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground-800 transition-colors hover:bg-primary-50"
                >
                  <i className="ri-basketball-line text-primary-500" />
                  {t.city}{t.name}
                </button>
              ))}
            </div>
          )}
          {results.matches.length > 0 && (
            <div className="border-t border-background-200 py-1">
              <p className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-foreground-400">赛事</p>
              {results.matches.map((m) => {
                const home = teams.find((t) => t.id === m.homeTeamId);
                const away = teams.find((t) => t.id === m.awayTeamId);
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => go(`/match/${m.id}`)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground-800 transition-colors hover:bg-primary-50"
                  >
                    <i className="ri-trophy-line text-accent-500" />
                    {home?.name} vs {away?.name}
                  </button>
                );
              })}
            </div>
          )}
          {hasResults && (
            <button
              type="button"
              onClick={() => go(`/live?q=${encodeURIComponent(query.trim())}`)}
              className="block w-full border-t border-background-200 px-3 py-2 text-left text-xs font-semibold text-primary-600 transition-colors hover:bg-primary-50"
            >
              查看“{query.trim()}”的全部直播赛事 →
            </button>
          )}
        </div>
      )}
      {variant === "mobile" && null}
    </div>
  );
}